/**
 * Firebase Mobile Presence module.
 * Manages presence for mobile clients.
 *
 * @module firebase/presence/mobile
 */

import { ref, onValue, update, get, serverTimestamp } from "firebase/database";
import { db } from "../config";
import { SESSION_TIMING, parseFirebaseTimestamp } from "../utils";
import { recalculateAdminGlobalStatus } from "./core";

const mobileSessionTimers = {};

/**
 * Updates the status of a mobile session
 *
 * @async
 * @param {string} adminId - The ID of the admin
 * @param {string} sessionId - The ID of the mobile session
 * @param {string} status - The new status to set
 * @returns {Promise<void>}
 */
export const updateMobileSessionStatus = async (adminId, sessionId, status) => {
  try {
    const sessionRef = ref(
      db,
      `admins/${adminId}/sessions/mobile/${sessionId}`
    );

    await update(sessionRef, {
      status,
      lastStatusChange: serverTimestamp(),
    });

    await recalculateAdminGlobalStatus(adminId);
  } catch (error) {
    console.error("Error updating mobile session status:", error);
  }
};

/**
 * Clears timers for a mobile session
 *
 * @param {string} timerKey - The key for the timer
 */
const clearMobileSessionTimers = (timerKey) => {
  if (mobileSessionTimers[timerKey]) {
    if (mobileSessionTimers[timerKey].awayTimer) {
      clearTimeout(mobileSessionTimers[timerKey].awayTimer);
    }
    if (mobileSessionTimers[timerKey].offlineTimer) {
      clearTimeout(mobileSessionTimers[timerKey].offlineTimer);
    }
    delete mobileSessionTimers[timerKey];
  }
};

/**
 * Sets up timers for a mobile session
 *
 * @param {string} adminId - The ID of the admin
 * @param {string} sessionId - The ID of the mobile session
 * @param {string} timerKey - The key for the timer
 * @param {number|Object} lastActiveTimestamp - Timestamp of the last activity
 * @param {Object} sessionData - The session data
 */
const setMobileSessionTimers = (
  adminId,
  sessionId,
  timerKey,
  lastActiveTimestamp,
  sessionData
) => {
  const sessionRef = ref(db, `admins/${adminId}/sessions/mobile/${sessionId}`);

  let lastActiveTime;
  let now = Date.now();

  if (lastActiveTimestamp) {
    lastActiveTime = parseFirebaseTimestamp(lastActiveTimestamp);
  }

  if (!lastActiveTime || isNaN(lastActiveTime)) {
    lastActiveTime = now;
  }

  const inactiveTime = now - lastActiveTime;
  const timeUntilAway = Math.max(0, SESSION_TIMING.AWAY_TIMEOUT - inactiveTime);
  const timeUntilOffline = Math.max(
    0,
    SESSION_TIMING.OFFLINE_TIMEOUT - inactiveTime
  );

  mobileSessionTimers[timerKey] = {
    awayTimer: null,
    offlineTimer: null,
  };

  /**
   * Updates global status after changing session status
   */
  const updateGlobalStatusAfterChange = async () => {
    try {
      await recalculateAdminGlobalStatus(adminId);
    } catch (error) {
      console.error(
        "Error updating global status after mobile status change:",
        error
      );
    }
  };

  /**
   * Updates session status and recalculates global status
   *
   * @param {string} status - The status to update to
   */
  const updateSessionStatusWithGlobalRecalculation = async (status) => {
    try {
      await update(sessionRef, {
        status,
        lastStatusChange: serverTimestamp(),
      });
      await updateGlobalStatusAfterChange();
    } catch (error) {
      console.error("Error updating mobile session status:", error);
    }
  };

  if (timeUntilAway <= 0 && sessionData.status === "online") {
    updateSessionStatusWithGlobalRecalculation("away");
    return;
  }

  if (timeUntilAway > 0 && sessionData.status === "online") {
    mobileSessionTimers[timerKey].awayTimer = setTimeout(async () => {
      try {
        await update(sessionRef, {
          status: "away",
          lastStatusChange: serverTimestamp(),
        });
        const verifyData = await get(sessionRef);
        if (verifyData.exists()) {
          // Verification logic can be added here if needed
        }

        await updateGlobalStatusAfterChange();
      } catch (error) {
        console.error("Error setting away status:", error);
      }
    }, timeUntilAway);

    setTimeout(async () => {
      try {
        const checkData = await get(sessionRef);
        if (checkData.exists()) {
          if (
            checkData.val().status === "online" &&
            checkData.val().appState !== "active"
          ) {
            await update(sessionRef, {
              status: "away",
              lastStatusChange: serverTimestamp(),
            });
            await updateGlobalStatusAfterChange();
          }
        }
      } catch (error) {
        console.error("Error verifying away status:", error);
      }
    }, timeUntilAway + SESSION_TIMING.VERIFICATION_DELAY);
  }

  if (timeUntilOffline > 0 && sessionData.status !== "offline") {
    mobileSessionTimers[timerKey].offlineTimer = setTimeout(async () => {
      try {
        await update(sessionRef, {
          status: "offline",
          lastStatusChange: serverTimestamp(),
        });
        await updateGlobalStatusAfterChange();
      } catch (error) {
        console.error("Error setting offline status:", error);
      }
    }, timeUntilOffline);
  } else if (timeUntilOffline <= 0 && sessionData.status !== "offline") {
    update(sessionRef, {
      status: "offline",
      lastStatusChange: serverTimestamp(),
    })
      .then(async () => {
        await updateGlobalStatusAfterChange();
      })
      .catch((error) => {
        console.error("Error immediately setting offline status:", error);
      });
  }
};

/**
 * Handles updates to a mobile session
 *
 * @param {string} adminId - The ID of the admin
 * @param {string} sessionId - The ID of the mobile session
 * @param {Object} sessionData - The session data
 */
const handleMobileSessionUpdate = (adminId, sessionId, sessionData) => {
  if (!sessionData || !sessionId || !adminId || !sessionData.connectionId)
    return;

  const timerKey = `${adminId}_${sessionId}`;
  clearMobileSessionTimers(timerKey);
  if (sessionData.appState !== "active") {
    setMobileSessionTimers(
      adminId,
      sessionId,
      timerKey,
      sessionData.lastActive,
      sessionData
    );
  }
};

/**
 * Monitors mobile app sessions for an admin
 *
 * @param {string} adminId - The ID of the admin
 * @returns {Function|undefined} Cleanup function
 */
export const monitorMobileAppSessions = (adminId) => {
  if (!adminId) return;

  const mobileSessionsRef = ref(db, `admins/${adminId}/sessions/mobile`);
  return onValue(mobileSessionsRef, (snapshot) => {
    const mobileSessions = snapshot.val() || {};
    Object.entries(mobileSessions).forEach(([sessionId, sessionData]) => {
      handleMobileSessionUpdate(adminId, sessionId, sessionData);
    });
  });
};

/**
 * Cleans up all mobile session timers for an admin
 *
 * @param {string} adminId - The ID of the admin
 */
export const cleanupMobileSessionTimers = (adminId) => {
  Object.keys(mobileSessionTimers).forEach((timerKey) => {
    if (timerKey.startsWith(`${adminId}_`)) {
      clearMobileSessionTimers(timerKey);
    }
  });
};
